import { FC } from 'react';
import { useParams } from 'react-router-dom';
import { i18n } from '@lingui/core';
import { Text } from '@mantine/core';
import { useMessageEntities } from '../Service/hooks/useMessageEntities';
import { MessageDTO } from '../Types/MessageT';
import {
  replacePlaceholdersWithHtml,
  replaceTemplatePlaceholdersWithHTMLValues,
} from '../Service/Placeholder/PlaceholderResover';
import { useEventData } from '../Service/Context/EventParticipationContextV2';
import { useEventScope } from '../Service/hooks/useEventScope';
import { ThemeText } from './ThemeText';

interface Props {
  email: MessageDTO;
  value: string | null;
  settings?: boolean;
}

const MessagePlaceholderText: FC<Props> = ({ email, value, settings = false }) => {
  const { eventId, isEventScope } = useEventScope();

  if (value === null) {
    return null;
  }
  // We are in an event
  if (isEventScope && eventId && Number(eventId) === email.eventId) {
    return <EventPlaceholderText value={value} />;
  }
  // We are in an event, but we're loading an email (cause a participant in on those two events), which is related to another event
  if (isEventScope && eventId && Number(eventId) !== email.eventId) {
    return <EventPlaceholderText value={value} />;
  }
  // We are in general postbox master details
  if (settings) {
    return <PlaceholderSettingsText value={value} />;
  }
  // General postbox master details
  return <PlaceholderText email={email} value={value} settings={settings} />;
};

export const EventPlaceholderText: FC<{ value: string }> = ({ value }) => {
  const { event, customer, location, customAttributeValues } = useEventData();

  const htmlValue = replaceTemplatePlaceholdersWithHTMLValues(i18n, value, {
    event,
    customer,
    eventLocation: location,
    customAttributeValues,
  });

  return <MessageText htmlValue={htmlValue} />;
};

const PlaceholderSettingsText: FC<{ value: string }> = ({ value }) => {
  const htmlValue = replacePlaceholdersWithHtml(i18n, value ?? '');
  return <MessageText htmlValue={htmlValue} />;
};

const PlaceholderText: FC<Props> = ({ email, value, settings }) => {
  const { event, customer, eventLocation, customAttributeValues } = useMessageEntities(email);

  const htmlValue = settings
    ? replacePlaceholdersWithHtml(i18n, value ?? '')
    : replaceTemplatePlaceholdersWithHTMLValues(i18n, value ?? '', {
        event: event ?? undefined,
        customer,
        eventLocation,
        customAttributeValues,
      });

  return <MessageText htmlValue={htmlValue} />;
};

export const MessageText: FC<{ htmlValue: string }> = ({ htmlValue }) => (
  <ThemeText
    dangerouslySetInnerHTML={{ __html: htmlValue }}
    sx={{
      maxWidth: '100%',
      wordBreak: 'break-word',
      whiteSpace: 'normal',
      overflowWrap: 'anywhere',
      '& img': {
        display: 'block',
        maxWidth: '100%',
        width: 'auto',
        height: 'auto',
      },
      '& table': {
        maxWidth: '100%',
      },
    }}
  />
);

export default MessagePlaceholderText;
